import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import * as Sentry from '@sentry/react';

import GiSymptomsInfo from './GiSymptomsInfo';
import { FORM_ACTIONS } from '../../utils/formActions.js';
import { useFormData } from '../../utils/formReducer.js';
import { useApi } from '../../hooks';
import { useTracking } from '../../hooks/useTracking';
import { usePrevious } from '../../hooks/usePrevious';
import EndOfFlow from './EndOfFlow';
import {
  FORM_VIEW,
  ENTRY_VIEWS,
  FORM_VIEW_PATH,
  FormViews,
  ONBOARDING_ROOT,
  TRACKING_SIGN_UP_FAILED,
  TRACKING_SIGN_UP_SUCCEDEED,
  TRACKING_ONBOARDING_STEP_REVISITED,
  TRACKING_START_SIGN_UP,
  getViewFromPath,
} from './constants';

import { NewLandingPage } from '../LandingPage/NewLandingPage/NewLandingPage';

import GiSymptomsList from './GiSymptomsList';
import { LoadingScreen } from '../ui/LoadingScreen';
import { Identity } from './PersonalInfo/Identity';
import { CurrentStatus } from './CurrentStatus';
import { useStartOnboarding } from '../../hooks/useStartOnboarding';
import { useReferrer } from '../../hooks/useReferrer';
import { UHC_STORE_UTM_SOURCE } from '../../constants/utms';
import { decodeBase64Email } from './utils';

export function Form() {
  const { identify, page: trackPageChange, track } = useTracking();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const isUhcStore = useReferrer()?.utm_source === UHC_STORE_UTM_SOURCE;

  const [apiError, setApiError] = useState(null);
  const { state, dispatch } = useFormData();
  const { api } = useApi({ throwIfError: true });
  const submitRef = useRef();
  const navigate = useNavigate();
  const { pathname, search, state: routerState } = useLocation();

  const currentViewKey = getViewFromPath(pathname);
  const prevViewKey = usePrevious(currentViewKey);
  // A later step reached without walking the flow has no answers behind it, so
  // it sends the user back to the first question. An unknown sub path does too.
  const shouldRestartFlow =
    !currentViewKey ||
    (!ENTRY_VIEWS.includes(currentViewKey) && !state.hasStartedOnboarding);

  const {
    startOnboarding: handleStartOnboarding,
    error: startOnboardingError,
  } = useStartOnboarding();

  const onboardingError =
    startOnboardingError || routerState?.customAccountError;

  // One page view per step per visit, which is what the funnel counted when the
  // steps shared a URL and could not be revisited. A step the user returns to
  // does not report again, and a step being redirected away from never reports
  // at all, or a refresh deep in the flow would land a view on a screen nobody
  // saw.
  const reportedStepsRef = useRef(new Set());

  useEffect(() => {
    if (shouldRestartFlow) return;

    if (currentViewKey && currentViewKey !== prevViewKey) {
      const trackingVal = FORM_VIEW[currentViewKey]?.trackingLabel;
      if (!trackingVal) return;
      if (reportedStepsRef.current.has(trackingVal)) {
        track(TRACKING_ONBOARDING_STEP_REVISITED, { step: trackingVal });
        return;
      }

      reportedStepsRef.current.add(trackingVal);

      // Special case to track email addresses coming from the newsletter
      if (currentViewKey === FormViews.landingPage) {
        const encodedEmail = searchParams.get('clientId');
        const decodedEmail = decodeBase64Email(encodedEmail);

        trackPageChange(
          trackingVal,
          decodedEmail ? { clientId: decodedEmail } : undefined
        );
        return;
      }

      trackPageChange(trackingVal);
    }
  }, [
    currentViewKey,
    prevViewKey,
    shouldRestartFlow,
    trackPageChange,
    searchParams,
    track,
  ]);

  const identifyUserOnTrackingTools = useCallback(
    (userId = null, userData = {}) => {
      identify(userId, userData);
      if (userId) {
        Sentry.setUser({ id: userId });
      }
    },
    [identify]
  );

  // Arriving from login with "create an account" goes straight to the form.
  useEffect(() => {
    if (!routerState?.toRegister) return;

    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'hasStartedOnboarding',
      payload: true,
    });
    navigate(`${FORM_VIEW_PATH[FormViews.endOfFlow]}${search}`, {
      replace: true,
    });
  }, [dispatch, navigate, routerState, search]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    scrollToTop();
  }, [currentViewKey]);

  const nextView = (viewKey) => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'hasStartedOnboarding',
      payload: true,
    });
    // `search` carries utm_source, which the register payload reads as the
    // referrer, so every step has to pass it along.
    navigate(`${FORM_VIEW_PATH[viewKey]}${search}`);
  };

  const handleSubmit = () => {
    if (state.email.indexOf('@') > -1) {
      handleUserRegister();
      return;
    } else {
      alert('Please Enter Your Email');
    }
  };

  const handleUserRegister = async () => {
    setIsLoading(true);
    setApiError(null);

    try {
      const {
        diagnosis_ids,
        symptom_ids,
        email,
        firstName,
        lastName,
        preferredName,
        state: addressState,
        birthday,
        current_status,
      } = state;

      const resolvedFirstName = preferredName?.trim() || firstName.trim();

      // TODO: Setup better random nonce/state value to confirm auth flow
      const nonce = parseInt(Math.random() * 1000 * (Math.random() * 1000));
      const payload = {
        email,
        firstName: resolvedFirstName,
        lastName: lastName.trim(),
        state: addressState,
        symptom_ids,
        referrer: searchParams.get('utm_source'),
        diagnosis_ids,
        birthday,
      };

      // Left out when the question was skipped, so the backend keeps whatever
      // it already has for this email.
      if (current_status) {
        payload.current_status = current_status;
      }

      ['sex', 'gender', 'race'].forEach((key) => {
        if (state[key] && state[key] !== 'Prefer not to say') {
          const col = key === 'race' ? 'ethnicity' : key; // translate column name for race/ethnicity
          payload[col] = state[key];
        }
      });

      const request = {
        route: `/register/${nonce}`,
        method: 'POST',
        payload,
      };

      // The free text the member wrote about their symptoms goes to /register
      // and no further. Segment and PostHog keep the answer, which is what the
      // funnel counts, and identify traits last for the life of the profile.
      const trackingPayload = current_status
        ? { ...payload, current_status: current_status.answer }
        : payload;

      track(TRACKING_START_SIGN_UP, trackingPayload);

      const response = await api(request);

      setIsLoading(false);

      identifyUserOnTrackingTools(response.id, trackingPayload);
      track(TRACKING_SIGN_UP_SUCCEDEED, response);

      // if uhc-store, navigate to /location, else continue as before
      if (isUhcStore) {
        navigate(`/location${search}`, {
          state: {
            email,
            userId: response.id,
            firstName,
            lastName,
          },
        });
      } else {
        navigate(`/health-plan${search}`, {
          state: {
            email,
            userId: response.id,
            firstName,
            lastName,
          },
        });
      }
    } catch (err) {
      track(TRACKING_SIGN_UP_FAILED, null);
      setApiError(err.message);
      console.error('Failed Register User', err);
    } finally {
      setIsLoading(false);
    }
  };

  const startOnboarding = (trackingEventStr) => {
    handleStartOnboarding(() => {
      navigate(`${ONBOARDING_ROOT}${search}`);
      if (trackingEventStr) track(trackingEventStr);
    });
  };

  const PageDisplay = () => {
    switch (currentViewKey) {
      case FormViews.landingPage:
        return (
          <NewLandingPage
            onStartOnboarding={startOnboarding}
            startOnboardingError={onboardingError}
          />
        );

      case FormViews.startOfFlow:
        return (
          <GiSymptomsList
            onNext={() => nextView(FormViews.questionGiCondition)}
          />
        );

      case FormViews.questionGiCondition:
        return (
          <GiSymptomsInfo
            onNext={() => nextView(FormViews.questionCurrentStatus)}
          />
        );

      case FormViews.questionCurrentStatus:
        return (
          <CurrentStatus onNext={() => nextView(FormViews.questionIdentity)} />
        );

      case FormViews.questionIdentity:
        return <Identity onNext={() => nextView(FormViews.endOfFlow)} />;

      case FormViews.endOfFlow:
        return (
          <EndOfFlow
            apiError={apiError}
            handleSubmit={handleSubmit}
            submitRef={submitRef}
          />
        );

      default:
        return null;
    }
  };

  if (shouldRestartFlow)
    return <Navigate to={`${ONBOARDING_ROOT}${search}`} replace />;

  if (isLoading)
    return (
      <LoadingScreen message={'One minute while we create your profile'} />
    );

  // Every view renders its own header/progress bar and sticky CTA via
  // StickyCtaLayout (LandingPage has its own custom layout), so Form itself
  // needs no wrapper at all.
  return PageDisplay();
}

export default Form;
